import { Button } from "@heroui/react";
import { Plural, Trans } from "@lingui/react/macro";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import useAccountInfo from "@/hooks/useAccountInfo";
import { useClientFeature } from "@/hooks/useClientFeature";
import { api } from "@/lib/api-client";
import { isBillingPagePath } from "@/pages/Dashboard/Billing/billingPageState";
import { canAccessClientBilling } from "@/utils/billing-access";
import {
  trackBillingBannerCtaClicked,
  trackBillingBannerDismissed,
  trackBillingBannerShown,
  type BillingBannerContext,
} from "@/utils/billing-tracking";
import { CardPaymentFailedBanner } from "./CardPaymentFailedBanner";

/**
 * Global nudge for tenants with overdue invoices (DEV-8798). Severity and
 * visibility are decided server-side (godmode config + buffers); this only
 * renders what the endpoint reports. Shows for every tenant user; the CTA is
 * gated to whoever can actually reach the billing page. Dismiss is in-memory,
 * so it returns on refresh/next login.
 */
export function UnpaidInvoiceBanner() {
  const navigate = useNavigate();
  // The billing page carries its own unpaid block, so the banner would repeat it.
  const isOnBillingPage = isBillingPagePath(useLocation().pathname);
  const accountInfo = useAccountInfo((state) => state.accountInfo);
  const { data } = api.client.useOverdueSummary();
  const { isFeatureEnabled } = useClientFeature();
  const { data: cardFailures } = api.billing.useGetCardPaymentFailures({
    enabled: isFeatureEnabled("limeBillingCard"),
  });
  const [dismissed, setDismissed] = useState(false);

  // A failing card is the more actionable of the two, so it takes the slot.
  const failures = cardFailures?.failures ?? [];
  const showCardFailure = failures.length > 0;

  const tracking: BillingBannerContext | null =
    !isOnBillingPage && !showCardFailure && data && data.level !== "none"
      ? {
          level: data.level,
          count: data.count,
          daysOverdue: data.daysOverdue,
        }
      : null;

  // The summary arrives after mount, so the impression fires when the banner
  // first becomes visible rather than on mount; the ref keeps it to one.
  const impressionTracked = useRef(false);
  useEffect(() => {
    if (impressionTracked.current || dismissed || !tracking) return;
    impressionTracked.current = true;
    trackBillingBannerShown(tracking);
  }, [dismissed, tracking]);

  if (isOnBillingPage) return null;
  if (showCardFailure) return <CardPaymentFailedBanner failures={failures} />;
  if (dismissed || !tracking) return null;

  const isDanger = tracking.level === "danger";
  const color = isDanger ? "danger" : "warning";
  const canOpenBilling = canAccessClientBilling(accountInfo);

  // z-40 keeps the banner above page content but below HeroUI overlays (z-50),
  // so modals/drawers cover it instead of the banner clipping their headers.
  return (
    <div className="sticky top-0 z-40 w-full">
      <LimeAlert
        color={color}
        classNames={{ base: "rounded-none border-x-0 border-t-0" }}
        isClosable
        onClose={() => {
          trackBillingBannerDismissed(tracking);
          setDismissed(true);
        }}
        title={<Trans>Imate neporavnane obveznosti</Trans>}
        description={
          <Plural
            value={tracking.count}
            one="# zapadel račun"
            two="# zapadla računa"
            few="# zapadli računi"
            other="# zapadlih računov"
          />
        }
        endContent={
          canOpenBilling ? (
            <Button
              color={color}
              size="sm"
              className="shrink-0"
              onPress={() => {
                trackBillingBannerCtaClicked(tracking);
                navigate("/dashboard/billing");
              }}
            >
              <Trans>Poglej in plačaj</Trans>
            </Button>
          ) : (
            <span className="text-foreground-500 shrink-0 text-xs">
              <Trans>Za poravnavo kontaktirajte skrbnika računa.</Trans>
            </span>
          )
        }
      />
    </div>
  );
}
